import { CSSProperties } from 'react';
import { DND_COLORS, DND_OPACITY, DND_TRANSITIONS, DND_CURSORS, DND_NO_SELECT_STYLES } from './DndConstants';

export function mergeReferences<T>(...references: (React.Ref<T> | undefined)[]): (node: T | null) => void {
  return (node: T | null) => {
    for (const reference of references) {
      if (typeof reference === 'function') {
        reference(node);
      } else if (reference && 'current' in reference) {
        (reference as React.MutableRefObject<T | null>).current = node;
      }
    }
  };
}

export function getDraggingStyles({
  isDragging,
  isDragEnabled,
  baseStyle = {},
  opacity = DND_OPACITY.dragging,
}: {
  isDragging: boolean;
  isDragEnabled: boolean;
  baseStyle?: CSSProperties;
  opacity?: number;
}): CSSProperties {
  return {
    ...baseStyle,
    opacity: isDragging ? opacity : DND_OPACITY.normal,
    cursor: isDragEnabled ? DND_CURSORS.grab : DND_CURSORS.default,
  };
}

export function getDropTargetStyles({
  isDropTarget,
  isDragging = false,
  baseStyle = {},
  includeTransition = false,
  includeNoSelect = false,
}: {
  isDropTarget: boolean;
  isDragging?: boolean;
  baseStyle?: CSSProperties;
  includeTransition?: boolean;
  includeNoSelect?: boolean;
}): CSSProperties {
  const styles: CSSProperties = {
    ...baseStyle,
    ...(isDragging && { opacity: DND_OPACITY.dragging }),
    ...(isDropTarget && {
      backgroundColor: DND_COLORS.dropTargetBackground,
      outline: `2px solid ${DND_COLORS.dropTargetPrimary}`,
      outlineOffset: '-2px',
    }),
    ...(includeTransition && { transition: DND_TRANSITIONS.fast }),
    ...(includeNoSelect && DND_NO_SELECT_STYLES),
  };

  return styles;
}

export const PINNED_CELL_SELECTORS = ['.MuiDataGrid-cell--pinnedLeft', '.MuiDataGrid-cell--pinnedRight'] as const;

const DROP_TARGET_OUTLINE = `2px solid ${DND_COLORS.dropTargetPrimary}`;
const DROP_TARGET_OUTLINE_OFFSET = '-2px';

function applyPinnedCellDropStyles(cell: HTMLElement, isDropTarget: boolean): void {
  const styleProperties: [string, string][] = [
    ['background-color', DND_COLORS.dropTargetTransparent],
    ['outline', DROP_TARGET_OUTLINE],
    ['outline-offset', DROP_TARGET_OUTLINE_OFFSET],
  ];

  if (isDropTarget) {
    for (const [property, value] of styleProperties) {
      cell.style.setProperty(property, value, 'important');
    }
  } else {
    for (const [property] of styleProperties) {
      cell.style.removeProperty(property);
    }
  }
}

export function applyPinnedCellStyles(rowElement: HTMLElement, isDropTarget: boolean): void {
  const pinnedCells = rowElement.querySelectorAll(PINNED_CELL_SELECTORS.join(', '));
  for (const cell of pinnedCells) {
    if (cell instanceof HTMLElement) {
      applyPinnedCellDropStyles(cell, isDropTarget);
    }
  }
}

export function applyRowPinnedCellStyles(rowElement: HTMLElement, isDropTarget: boolean): void {
  applyPinnedCellStyles(rowElement, isDropTarget);
}

export function getSelectedRowIds(
  selectionModel:
    | {
        type: 'include' | 'exclude';
        ids: Set<string | number>;
      }
    | undefined,
  getAllRowIds: () => (string | number)[]
): string[] {
  if (!selectionModel) {
    return [];
  }

  if (selectionModel.type === 'include') {
    return [...selectionModel.ids].map(String);
  }

  const allIds = getAllRowIds();
  return allIds.filter(id => !selectionModel.ids.has(id)).map(String);
}
